import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { 
  getWorkspace, 
  inviteToWorkspace, 
  updateMemberName, 
  leaveWorkspace,
  removeMember,
  cancelInvite
} from '@/lib/api';
import { toast } from 'sonner';
import { Users, Mail, Crown, Copy, Check, UserPlus, Pencil, LogOut, X } from 'lucide-react';

function MemberItem({ member, isCurrentUser, isOwner, onEditName, onRemove, onLeave }) {
  const displayName = member.name || member.email || 'Unknown';
  const initial = displayName.charAt(0).toUpperCase();
  const memberIsOwner = member.is_owner;
  
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <span className="text-xs font-medium text-primary">{initial}</span>
        </div>
        <div>
          <p className="text-sm font-medium">{displayName}</p>
          {memberIsOwner && (
            <Badge variant="secondary" className="text-xs mt-0.5">
              <Crown className="w-3 h-3 mr-1" />
              Owner
            </Badge>
          )}
          {isCurrentUser && !memberIsOwner && (
            <Badge variant="outline" className="text-xs mt-0.5">You</Badge>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {isCurrentUser && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onEditName}
            title="Edit your name"
          >
            <Pencil className="w-3.5 h-3.5" />
          </Button>
        )}
        {isCurrentUser && !memberIsOwner && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={onLeave}
            title="Leave workspace"
          >
            <LogOut className="w-3.5 h-3.5" />
          </Button>
        )}
        {!isCurrentUser && isOwner && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={function() { onRemove(member.user_id); }}
            title="Remove member"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

function PendingInviteItem({ invite, onCancel }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg border border-dashed">
      <div className="flex items-center gap-2">
        <Mail className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm">{invite.email}</span>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="text-xs">Pending</Badge>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-destructive"
          onClick={function() { onCancel(invite.email); }}
          title="Cancel invite"
        >
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

export const WorkspacePanel = ({ open, onOpenChange }) => {
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingName, setSavingName] = useState(false);

  useEffect(function() {
    if (open) {
      loadWorkspace();
    }
  }, [open]);

  const loadWorkspace = async function() {
    setLoading(true);
    try {
      const res = await getWorkspace();
      setWorkspace(res.data);
    } catch (err) {
      console.error('Failed to load workspace:', err);
      toast.error('Failed to load workspace info');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async function(e) {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    
    setSending(true);
    try {
      const res = await inviteToWorkspace(email);
      toast.success('Invite created! Share the link with your teammate.');
      setLastInviteUrl(window.location.origin + res.data.invite_url);
      setInviteEmail('');
      loadWorkspace();
    } catch (err) {
      console.error('Failed to create invite:', err);
      const detail = err.response && err.response.data && err.response.data.detail;
      const message = detail || 'Failed to create invite';
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const copyInviteLink = async function() {
    if (!lastInviteUrl) return;
    try {
      await navigator.clipboard.writeText(lastInviteUrl);
      setCopied(true);
      toast.success('Invite link copied!');
      setTimeout(function() { setCopied(false); }, 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleEditName = function() {
    const currentUser = members.find(function(m) { return m.user_id === currentUserId; });
    setNewName(currentUser ? (currentUser.name || currentUser.email || '') : '');
    setEditingName(true);
  };

  const handleSaveName = async function(e) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    
    setSavingName(true);
    try {
      await updateMemberName(name);
      toast.success('Name updated');
      setEditingName(false);
      loadWorkspace();
    } catch (err) {
      console.error('Failed to update name:', err);
      toast.error('Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  const handleLeave = async function() {
    if (!window.confirm('Are you sure you want to leave this workspace? You will lose access to the shared data.')) {
      return;
    }
    
    try {
      await leaveWorkspace();
      toast.success('You have left the workspace');
      onOpenChange(false);
      window.location.reload();
    } catch (err) {
      console.error('Failed to leave workspace:', err);
      const detail = err.response && err.response.data && err.response.data.detail;
      toast.error(detail || 'Failed to leave workspace');
    }
  };

  const handleRemoveMember = async function(userId) {
    if (!window.confirm('Are you sure you want to remove this member?')) {
      return;
    }
    
    try {
      await removeMember(userId);
      toast.success('Member removed');
      loadWorkspace();
    } catch (err) {
      console.error('Failed to remove member:', err);
      const detail = err.response && err.response.data && err.response.data.detail;
      toast.error(detail || 'Failed to remove member');
    }
  };

  const memberCount = workspace ? (workspace.member_count || 0) : 0;
  const pendingInvites = workspace ? (workspace.pending_invites || []) : [];
  const pendingCount = pendingInvites.length;
  const canInvite = memberCount + pendingCount < 3;
  const members = workspace ? (workspace.members || []) : [];
  const currentUserId = workspace ? workspace.current_user_id : null;
  const isOwner = workspace ? (workspace.owner_user_id === currentUserId) : false;

  const memberItems = [];
  for (let i = 0; i < members.length; i++) {
    const member = members[i];
    memberItems.push(
      <MemberItem 
        key={member.user_id} 
        member={member}
        isCurrentUser={member.user_id === currentUserId}
        isOwner={isOwner}
        onEditName={handleEditName}
        onRemove={handleRemoveMember}
        onLeave={handleLeave}
      />
    );
  }

  const handleCancelInvite = async function(email) {
    if (!window.confirm('Cancel this invitation?')) {
      return;
    }
    
    try {
      await cancelInvite(email);
      toast.success('Invitation cancelled');
      setLastInviteUrl(null);
      loadWorkspace();
    } catch (err) {
      console.error('Failed to cancel invite:', err);
      const detail = err.response && err.response.data && err.response.data.detail;
      toast.error(detail || 'Failed to cancel invite');
    }
  };

  const pendingItems = [];
  for (let i = 0; i < pendingInvites.length; i++) {
    pendingItems.push(
      <PendingInviteItem 
        key={i} 
        invite={pendingInvites[i]} 
        onCancel={handleCancelInvite}
      />
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[420px]" data-testid="workspace-panel">
        <SheetHeader>
          <SheetTitle className="font-heading text-xl flex items-center gap-2">
            <Users className="w-5 h-5" />
            Share Access
          </SheetTitle>
          <SheetDescription>
            Invite others to view and edit this forecast
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="py-12 text-center text-muted-foreground">
            Loading...
          </div>
        ) : (
          <div className="py-6 space-y-6">
            {/* Edit Name Form */}
            {editingName && (
              <div className="space-y-3 p-3 rounded-lg border bg-muted/30">
                <Label className="text-sm font-medium">Edit Your Display Name</Label>
                <form onSubmit={handleSaveName} className="flex gap-2">
                  <Input
                    value={newName}
                    onChange={function(e) { setNewName(e.target.value); }}
                    placeholder="Your name"
                    disabled={savingName}
                  />
                  <Button type="submit" disabled={savingName || !newName.trim()}>
                    {savingName ? '...' : 'Save'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={function() { setEditingName(false); }}
                  >
                    Cancel
                  </Button>
                </form>
              </div>
            )}

            {/* Team Members */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                Team Members ({memberCount}/3)
              </Label>
              <div className="space-y-2">{memberItems}</div>
            </div>

            {/* Pending Invites */}
            {pendingInvites.length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-medium text-muted-foreground">
                  Pending Invites
                </Label>
                <div className="space-y-2">{pendingItems}</div>
              </div>
            )}

            <Separator />

            {/* Invite Form */}
            <div className="space-y-3">
              <Label htmlFor="invite_email" className="text-sm font-medium flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Add Team Member
              </Label>
              
              {canInvite ? (
                <form onSubmit={handleInvite} className="space-y-3">
                  <Input
                    id="invite_email"
                    type="email"
                    placeholder="teammate@email.com"
                    value={inviteEmail}
                    onChange={function(e) { setInviteEmail(e.target.value); }}
                    disabled={sending}
                    data-testid="invite-email-input"
                  />
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={sending || !inviteEmail.trim()}
                    data-testid="send-invite-btn"
                  >
                    {sending ? 'Creating...' : 'Create Invite'}
                  </Button>
                </form>
              ) : (
                <div className="p-3 rounded-lg bg-muted text-center">
                  <p className="text-sm text-muted-foreground">
                    Free accounts limited to 3 users
                  </p>
                </div>
              )}

              {lastInviteUrl && (
                <div className="space-y-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
                  <p className="text-sm font-medium text-primary">
                    Share this link with your teammate:
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={lastInviteUrl}
                      readOnly
                      className="text-xs font-mono"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={copyInviteLink}
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Note: Automated email sending is not configured. Please copy and share the link manually.
                  </p>
                </div>
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              All team members can view and edit transactions, settings, and the forecast.
              Changes are shared across all members.
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
